/**
 * Public-web projection of the canonical launch-market contract in
 * `lib/market-country-config.cjs` / Android `CountryCatalog`.
 *
 * `tests/public-web-location-preferences.test.mjs` compares this data to the
 * server contract so a country/language change cannot silently drift.
 */
export const PRODUCT_LANGUAGE_OPTIONS = Object.freeze([
  { code: "en", label: "English", localeTag: "en-US", direction: "ltr" },
  { code: "hi", label: "Hindi", localeTag: "hi-IN", direction: "ltr" },
  { code: "bn", label: "Bengali", localeTag: "bn-IN", direction: "ltr" },
  { code: "te", label: "Telugu", localeTag: "te-IN", direction: "ltr" },
  { code: "mr", label: "Marathi", localeTag: "mr-IN", direction: "ltr" },
  { code: "ta", label: "Tamil", localeTag: "ta-IN", direction: "ltr" },
  { code: "gu", label: "Gujarati", localeTag: "gu-IN", direction: "ltr" },
  { code: "ur", label: "Urdu", localeTag: "ur-IN", direction: "rtl" },
  { code: "kn", label: "Kannada", localeTag: "kn-IN", direction: "ltr" },
  { code: "or", label: "Odia", localeTag: "or-IN", direction: "ltr" },
  { code: "ml", label: "Malayalam", localeTag: "ml-IN", direction: "ltr" },
  { code: "pa", label: "Punjabi (Gurmukhi)", localeTag: "pa-IN", direction: "ltr" },
  {
    code: "pnb",
    label: "Punjabi (Shahmukhi)",
    // `pa-Arab-PK` is the Intl-supported BCP-47 form for Shahmukhi.
    // Keep `pnb` as the product/storage code and translate only at this edge.
    localeTag: "pa-Arab-PK",
    direction: "rtl",
  },
  { code: "as", label: "Assamese", localeTag: "as-IN", direction: "ltr" },
  { code: "es", label: "Spanish", localeTag: "es-ES", direction: "ltr" },
  {
    code: "zh",
    label: "Chinese (Simplified)",
    localeTag: "zh-Hans-CN",
    direction: "ltr",
  },
  { code: "fr", label: "French", localeTag: "fr-FR", direction: "ltr" },
  { code: "de", label: "German", localeTag: "de-DE", direction: "ltr" },
  { code: "ja", label: "Japanese", localeTag: "ja-JP", direction: "ltr" },
]);

export const MARKET_COUNTRY_OPTIONS = Object.freeze([
  {
    code: "IN",
    label: "India",
    defaultNewsSource: "category-global-in-national",
    languages: Object.freeze([
      "en",
      "hi",
      "bn",
      "te",
      "mr",
      "ta",
      "gu",
      "ur",
      "kn",
      "or",
      "ml",
      "pa",
      "pnb",
      "as",
    ]),
  },
  {
    code: "US",
    label: "United States",
    defaultNewsSource: "category-global-us-national",
    languages: Object.freeze(["en", "es", "zh", "fr"]),
  },
  {
    code: "CA",
    label: "Canada",
    defaultNewsSource: "category-global-ca-national",
    languages: Object.freeze(["en", "fr"]),
  },
  {
    code: "GB",
    label: "United Kingdom",
    defaultNewsSource: "category-global-gb-national",
    languages: Object.freeze(["en"]),
  },
  {
    code: "AU",
    label: "Australia",
    defaultNewsSource: "category-global-au-national",
    languages: Object.freeze(["en"]),
  },
  {
    code: "DE",
    label: "Germany",
    defaultNewsSource: "category-global-de-national",
    languages: Object.freeze(["de", "en"]),
  },
  {
    code: "FR",
    label: "France",
    defaultNewsSource: "category-global-fr-national",
    languages: Object.freeze(["fr", "en"]),
  },
  {
    code: "JP",
    label: "Japan",
    defaultNewsSource: "category-global-jp-world",
    languages: Object.freeze(["ja", "en"]),
  },
]);

const PRODUCT_LANGUAGE_CODES = new Set(PRODUCT_LANGUAGE_OPTIONS.map((language) => language.code));

export function restoredProductLanguage(value) {
  const code = String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll("_", "-");
  if (code === "pa-arab" || code.startsWith("pa-arab-")) return "pnb";
  if (PRODUCT_LANGUAGE_CODES.has(code)) return code;
  const primaryLanguage = code.split("-")[0] || "";
  return PRODUCT_LANGUAGE_CODES.has(primaryLanguage) ? primaryLanguage : null;
}

export function onboardingLocationSetupKind(country) {
  return String(country || "")
    .trim()
    .toUpperCase() === "IN"
    ? "india_local_news"
    : "places";
}
